/**
 * Diálogo "Ingresar Macros" con 3 pestañas:
 *   1. BUSCAR (autocompletado contra food_database + custom_foods).
 *   2. ARMAR RECETA (compone una comida con varios ingredientes).
 *   3. MANUAL (ingreso libre de calorías/macros).
 *
 * También expone "Calcular estimado" cuando la usuaria no encuentra su comida.
 * Importante: en pantalla NUNCA se menciona "IA". El feature se llama
 * "Calcular estimado" o "Estimado".
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  FaSearch, FaUtensils, FaPlus, FaTrash, FaSave, FaPenAlt, FaBookOpen, FaCalculator,
  FaBoxOpen, FaTimes, FaCheck
} from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('deisycfit_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MACRO_COLORS = {
  calories: 'var(--brand-primary)',
  protein: '#00F0FF',
  carbs: '#CCFF00',
  fats: '#FF6B9D',
};

/** Multiplica los macros base (por 100g/100ml) por la cantidad ingresada. */
function scaleMacros(food, qty, unit) {
  const q = parseFloat(qty) || 0;
  const portion = parseFloat(food?.portion_g) || 100;
  let factor = 0;
  if (unit === 'units' || unit === 'unidades') {
    // 1 unidad ≈ portion_g del alimento
    factor = q;
  } else {
    factor = q / portion;
  }
  return {
    calories: Math.round((food?.calories || 0) * factor),
    protein: +((food?.protein || 0) * factor).toFixed(1),
    carbs: +((food?.carbs || 0) * factor).toFixed(1),
    fats: +((food?.fats || 0) * factor).toFixed(1),
  };
}

export default function MacroEntryDialog({ open, onOpenChange, onSaved, today }) {
  const [tab, setTab] = useState('search');

  // -------- BUSCAR --------
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [foodQty, setFoodQty] = useState('100');
  const [foodUnit, setFoodUnit] = useState('g');

  // Estimación
  const [estimating, setEstimating] = useState(false);
  const [estimateResult, setEstimateResult] = useState(null);
  // Solo Premium/Gold/VIP tienen acceso a "Calcular Estimado" (endpoint LLM)
  const [canUseEstimate, setCanUseEstimate] = useState(false);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(r => {
        if (ignore) return;
        const plan = (r.data?.subscription_plan || r.data?.plan_type || '').toLowerCase();
        // Excluidos: fit_evolution, basic, basico, free, ''
        const excluded = new Set(['fit_evolution', 'basic', 'basico', 'free', '']);
        setCanUseEstimate(!excluded.has(plan));
      })
      .catch(() => setCanUseEstimate(false));
    return () => { ignore = true; };
  }, [open]);

  // -------- RECETA --------
  const [recipeName, setRecipeName] = useState('');
  const [recipeItems, setRecipeItems] = useState([]); // [{food_name, quantity, unit, calories, protein, carbs, fats}]
  const [ingQuery, setIngQuery] = useState('');
  const [ingResults, setIngResults] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savingRecipe, setSavingRecipe] = useState(false);

  // -------- MANUAL --------
  const [manual, setManual] = useState({ name: '', calories: '', protein: '', carbs: '', fats: '' });

  const [saving, setSaving] = useState(false);

  const recipeTotals = useMemo(() => recipeItems.reduce(
    (acc, it) => ({
      calories: acc.calories + (Number(it.calories) || 0),
      protein: acc.protein + (Number(it.protein) || 0),
      carbs: acc.carbs + (Number(it.carbs) || 0),
      fats: acc.fats + (Number(it.fats) || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fats: 0 }
  ), [recipeItems]);

  // Búsqueda debounced
  const runSearch = useCallback(async (q, setter, loadingSetter) => {
    loadingSetter(true);
    try {
      const res = await axios.get(`${API}/macro-log/foods/search`, {
        params: { q, limit: 25 }, headers: authHeaders(),
      });
      setter(res.data?.foods || []);
    } catch {
      setter([]);
    } finally {
      loadingSetter(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (tab !== 'search') return;
    const t = setTimeout(() => runSearch(query, setResults, setSearching), 220);
    return () => clearTimeout(t);
  }, [query, open, tab, runSearch]);

  useEffect(() => {
    if (!open) return;
    if (tab !== 'recipe') return;
    const t = setTimeout(() => runSearch(ingQuery, setIngResults, () => {}), 220);
    return () => clearTimeout(t);
  }, [ingQuery, open, tab, runSearch]);

  // Cargar recetas guardadas al abrir
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/macro-log/recipes`, { headers: authHeaders() });
        setSavedRecipes(res.data?.recipes || []);
      } catch {
        setSavedRecipes([]);
      }
    })();
  }, [open]);

  const resetAll = () => {
    setTab('search');
    setQuery(''); setResults([]); setSelectedFood(null); setFoodQty('100'); setFoodUnit('g');
    setEstimating(false); setEstimateResult(null);
    setRecipeName(''); setRecipeItems([]); setIngQuery(''); setIngResults([]);
    setManual({ name: '', calories: '', protein: '', carbs: '', fats: '' });
  };

  const saveEntry = async (entry) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/macro-log`, { ...entry, date: today }, { headers: authHeaders() });
      toast.success('Comida agregada');
      onSaved?.(res.data?.entry);
      onOpenChange(false);
      resetAll();
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  // -------- BUSCAR: agregar comida seleccionada --------
  const handleAddSelected = () => {
    if (!selectedFood) return;
    const m = scaleMacros(selectedFood, foodQty, foodUnit);
    saveEntry({
      name: `${selectedFood.name} (${foodQty}${foodUnit === 'g' || foodUnit === 'ml' ? foodUnit : ' ' + foodUnit})`,
      ...m,
    });
  };

  // -------- BUSCAR: estimar --------
  const handleEstimate = async () => {
    if (!query.trim()) {
      toast.error('Escribe primero el alimento que quieres estimar');
      return;
    }
    setEstimating(true);
    setEstimateResult(null);
    try {
      const res = await axios.post(`${API}/macro-log/estimate`, { description: query.trim() }, { headers: authHeaders() });
      setEstimateResult(res.data?.estimate || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pudimos calcular el estimado');
    } finally {
      setEstimating(false);
    }
  };

  const handleSaveEstimate = () => {
    if (!estimateResult) return;
    saveEntry({
      name: estimateResult.name,
      calories: estimateResult.calories,
      protein: estimateResult.protein,
      carbs: estimateResult.carbs,
      fats: estimateResult.fats,
    });
  };

  // -------- RECETA: agregar ingrediente --------
  const addIngredient = (food) => {
    const m = scaleMacros(food, 100, 'g');
    setRecipeItems((prev) => [...prev, {
      food_name: food.name,
      quantity: 100,
      unit: 'g',
      portion_g: food.portion_g || 100,
      base_calories: food.calories,
      base_protein: food.protein,
      base_carbs: food.carbs,
      base_fats: food.fats,
      ...m,
    }]);
    setIngQuery('');
    setIngResults([]);
  };

  const updateIngredient = (idx, patch) => {
    setRecipeItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const merged = { ...it, ...patch };
      const base = {
        portion_g: merged.portion_g || 100,
        calories: merged.base_calories,
        protein: merged.base_protein,
        carbs: merged.base_carbs,
        fats: merged.base_fats,
      };
      const m = scaleMacros(base, merged.quantity, merged.unit);
      return { ...merged, ...m };
    }));
  };

  const removeIngredient = (idx) => {
    setRecipeItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddRecipeToLog = () => {
    if (recipeItems.length === 0) {
      toast.error('Agrega al menos un ingrediente');
      return;
    }
    saveEntry({
      name: recipeName.trim() || 'Receta compuesta',
      calories: recipeTotals.calories,
      protein: recipeTotals.protein,
      carbs: recipeTotals.carbs,
      fats: recipeTotals.fats,
    });
  };

  const handleSaveRecipe = async (isPublic) => {
    if (!recipeName.trim()) {
      toast.error('Ponle un nombre a la receta');
      return;
    }
    if (recipeItems.length === 0) {
      toast.error('Agrega al menos un ingrediente');
      return;
    }
    setSavingRecipe(true);
    try {
      await axios.post(`${API}/macro-log/recipes`, {
        name: recipeName.trim(),
        is_public: !!isPublic,
        ingredients: recipeItems.map((it) => ({
          food_name: it.food_name,
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit,
          calories: it.calories,
          protein: it.protein,
          carbs: it.carbs,
          fats: it.fats,
        })),
      }, { headers: authHeaders() });
      toast.success(isPublic ? 'Receta guardada para todas' : 'Receta guardada');
      // refrescar lista
      const res = await axios.get(`${API}/macro-log/recipes`, { headers: authHeaders() });
      setSavedRecipes(res.data?.recipes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la receta');
    } finally {
      setSavingRecipe(false);
    }
  };

  const loadSavedRecipe = (r) => {
    setRecipeName(r.name);
    setRecipeItems((r.ingredients || []).map((ing) => ({
      food_name: ing.food_name,
      quantity: ing.quantity,
      unit: ing.unit || 'g',
      portion_g: 100,
      base_calories: ing.quantity > 0 ? (ing.calories / ing.quantity) * 100 : 0,
      base_protein: ing.quantity > 0 ? (ing.protein / ing.quantity) * 100 : 0,
      base_carbs: ing.quantity > 0 ? (ing.carbs / ing.quantity) * 100 : 0,
      base_fats: ing.quantity > 0 ? (ing.fats / ing.quantity) * 100 : 0,
      calories: ing.calories,
      protein: ing.protein,
      carbs: ing.carbs,
      fats: ing.fats,
    })));
    toast.success('Receta cargada — ajusta cantidades si quieres');
  };

  // -------- MANUAL --------
  const handleSaveManual = () => {
    const c = parseFloat(manual.calories) || 0;
    const p = parseFloat(manual.protein) || 0;
    const k = parseFloat(manual.carbs) || 0;
    const g = parseFloat(manual.fats) || 0;
    if (c <= 0 && p <= 0 && k <= 0 && g <= 0) {
      toast.error('Ingresa al menos un macro o las calorías');
      return;
    }
    saveEntry({
      name: manual.name?.trim() || 'Comida extra',
      calories: c, protein: p, carbs: k, fats: g,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetAll(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FaPlus className="text-pink-400" /> Ingresar Macros
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Busca un alimento, arma una receta con ingredientes, o ingresa los macros manualmente.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-3 bg-gray-900">
            <TabsTrigger value="search" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white" data-testid="tab-search-food">
              <FaSearch className="mr-2" /> Buscar
            </TabsTrigger>
            <TabsTrigger value="recipe" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white" data-testid="tab-build-recipe">
              <FaUtensils className="mr-2" /> Armar receta
            </TabsTrigger>
            <TabsTrigger value="manual" className="data-[state=active]:bg-pink-600 data-[state=active]:text-white" data-testid="tab-manual">
              <FaPenAlt className="mr-2" /> Manual
            </TabsTrigger>
          </TabsList>

          {/* ===== BUSCAR ===== */}
          <TabsContent value="search" className="flex-1 overflow-y-auto pr-1 space-y-3 mt-3">
            {/* Aviso para la usuaria */}
            {canUseEstimate ? (
              <div className="p-3 rounded-lg flex items-start gap-2 text-xs"
                style={{ background: 'rgba(139, 92, 246, 0.10)', border: '1px solid rgba(139, 92, 246, 0.30)' }}
                data-testid="search-help-banner">
                <FaCalculator className="text-purple-300 mt-0.5 flex-shrink-0" />
                <p className="text-purple-100 leading-relaxed">
                  💡 <b>¿No encuentras tu alimento?</b> Escribe su nombre arriba y dale al botón <b className="text-purple-300">&ldquo;Calcular estimado&rdquo;</b>. Todos los alimentos de la base están registrados <b>por 100g (o 100ml)</b>; al elegir uno podrás cambiar el gramaje y los macros se recalculan automáticamente.
                </p>
              </div>
            ) : (
              <div className="p-3 rounded-lg flex items-start gap-2 text-xs"
                style={{ background: 'rgba(var(--brand-primary-glow), 0.10)', border: '1px solid rgba(var(--brand-primary-glow), 0.30)' }}
                data-testid="search-help-banner">
                <FaCalculator className="text-pink-300 mt-0.5 flex-shrink-0" />
                <p className="text-pink-100 leading-relaxed">
                  💡 <b>Busca tu alimento aquí.</b> Todos los alimentos están registrados por 100g (o 100ml); al elegir uno podrás cambiar el gramaje y los macros se recalculan automáticamente. Si no lo encuentras, usa la pestaña <b className="text-pink-300">Manual</b>.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelectedFood(null); setEstimateResult(null); }}
                placeholder="Ej: pollo pechuga, huevo, atún..."
                className="bg-white/5 border-white/20 text-white"
                data-testid="search-input"
              />
              {canUseEstimate && (
                <Button onClick={handleEstimate} disabled={estimating || !query.trim()} variant="outline" className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 whitespace-nowrap" data-testid="estimate-btn">
                  <FaCalculator className="mr-2" /> {estimating ? 'Calculando...' : 'Calcular estimado'}
                </Button>
              )}
            </div>

            {/* Resultados de búsqueda */}
            {!selectedFood && !estimateResult && (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {searching && <p className="text-xs text-gray-400 px-2">Buscando...</p>}
                {!searching && results.length === 0 && query.trim().length > 0 && (
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300">
                    {canUseEstimate ? (
                      <>No encontramos coincidencias. Usa <b className="text-purple-300">&ldquo;Calcular estimado&rdquo;</b> para obtener una aproximación, o ve a la pestaña <b className="text-pink-300">Manual</b>.</>
                    ) : (
                      <>No encontramos coincidencias. Prueba con otro nombre o usa la pestaña <b className="text-pink-300">Manual</b> para ingresarlo directamente.</>
                    )}
                  </div>
                )}
                {results.map((f, idx) => (
                  <button
                    key={`${f.name}-${idx}`}
                    onClick={() => { setSelectedFood(f); setFoodQty(String(f.portion_g || 100)); setFoodUnit(f.unit === '100ml' ? 'ml' : 'g'); }}
                    className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid={`food-result-${idx}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {f.name}{f.is_custom && <span className="ml-2 text-[10px] text-pink-400">CUSTOM</span>}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {f.calories} kcal · {f.protein}g P · {f.carbs}g C · {f.fats}g G <span className="text-pink-300 font-semibold">por {f.unit}</span>
                        </p>
                      </div>
                      <FaPlus className="text-pink-400 mt-1 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Estimación */}
            {estimateResult && (
              <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30" data-testid="estimate-result">
                <p className="text-xs uppercase tracking-wider text-purple-300 mb-2">Estimado · Puedes ajustar antes de guardar</p>
                <Input value={estimateResult.name} onChange={(e) => setEstimateResult({ ...estimateResult, name: e.target.value })} className="bg-white/5 border-white/20 text-white mb-3" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {['calories', 'protein', 'carbs', 'fats'].map((k) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider" style={{ color: MACRO_COLORS[k] }}>
                        {k === 'calories' ? 'Kcal' : k === 'protein' ? 'Proteína (g)' : k === 'carbs' ? 'Carbos (g)' : 'Grasas (g)'}
                      </label>
                      <Input type="number" value={estimateResult[k]} onChange={(e) => setEstimateResult({ ...estimateResult, [k]: parseFloat(e.target.value) || 0 })} className="bg-white/5 border-white/20 text-white mt-1" />
                    </div>
                  ))}
                </div>
                {estimateResult.notes && <p className="text-[11px] text-gray-400 mt-2 italic">{estimateResult.notes}</p>}
                <div className="flex gap-2 mt-3">
                  <Button onClick={() => setEstimateResult(null)} variant="outline" className="border-white/20 text-white hover:bg-white/10 flex-1">Descartar</Button>
                  <Button onClick={handleSaveEstimate} disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white flex-1" data-testid="save-estimate-btn">
                    <FaCheck className="mr-2" /> Guardar
                  </Button>
                </div>
              </div>
            )}

            {/* Alimento seleccionado: cantidad + macros calculados */}
            {selectedFood && (
              <div className="p-4 rounded-xl bg-pink-500/10 border border-pink-500/30" data-testid="selected-food-card">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">{selectedFood.name}</p>
                    <p className="text-[11px] text-gray-400">{selectedFood.calories} kcal · {selectedFood.protein}g P · {selectedFood.carbs}g C · {selectedFood.fats}g G <span className="text-pink-300 font-semibold">por {selectedFood.unit}</span></p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedFood(null)} className="border-white/20 text-white hover:bg-white/10">
                    <FaTimes />
                  </Button>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-pink-300 mb-2 font-semibold">✎ Ajusta el gramaje según lo que comiste</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase text-gray-400">Cantidad</label>
                    <Input type="number" min="0" step="any" value={foodQty} onChange={(e) => setFoodQty(e.target.value)} className="bg-white/5 border-white/20 text-white" data-testid="quantity-input" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-gray-400">Unidad</label>
                    <select value={foodUnit} onChange={(e) => setFoodUnit(e.target.value)} className="w-full mt-1 rounded-md px-3 py-2 bg-white/5 border border-white/20 text-white text-sm" data-testid="unit-select">
                      <option value="g">gramos</option>
                      <option value="ml">ml</option>
                      <option value="unidades">unidades</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 p-3 rounded-lg bg-black/30 border border-white/5">
                  {(() => {
                    const m = scaleMacros(selectedFood, foodQty, foodUnit);
                    return ['calories', 'protein', 'carbs', 'fats'].map((k) => (
                      <div key={k} className="text-center">
                        <p className="text-lg font-bold" style={{ color: MACRO_COLORS[k] }}>{m[k]}{k === 'calories' ? '' : 'g'}</p>
                        <p className="text-[9px] uppercase text-gray-400">{k === 'calories' ? 'Kcal' : k === 'protein' ? 'Prot' : k === 'carbs' ? 'Carbs' : 'Grasas'}</p>
                      </div>
                    ));
                  })()}
                </div>
                <Button onClick={handleAddSelected} disabled={saving} className="w-full mt-3 bg-pink-500 hover:bg-pink-600 text-white" data-testid="add-selected-food-btn">
                  <FaCheck className="mr-2" /> Agregar a mi día
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ===== ARMAR RECETA ===== */}
          <TabsContent value="recipe" className="flex-1 overflow-y-auto pr-1 space-y-3 mt-3">
            {/* Recetas guardadas */}
            {savedRecipes.length > 0 && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2">
                  <FaBookOpen /> Recetas guardadas
                </p>
                <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                  {savedRecipes.map((r) => (
                    <button key={r.id} onClick={() => loadSavedRecipe(r)} className="text-xs px-3 py-1.5 rounded-full bg-pink-500/15 border border-pink-500/30 text-pink-200 hover:bg-pink-500/25" data-testid={`saved-recipe-${r.id}`}>
                      {r.name} <span className="opacity-60">· {Math.round(r.totals?.calories || 0)}kcal</span>
                      {!r.is_mine && <span className="ml-1 text-[9px] uppercase text-pink-300">deisy</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Input value={recipeName} onChange={(e) => setRecipeName(e.target.value)} placeholder="Nombre de la receta (ej: Tostadas francesas)" className="bg-white/5 border-white/20 text-white" data-testid="recipe-name-input" />

            {/* Buscar ingrediente */}
            <div>
              <Input value={ingQuery} onChange={(e) => setIngQuery(e.target.value)} placeholder="Busca ingrediente (ej: pan integral)" className="bg-white/5 border-white/20 text-white" data-testid="ingredient-search-input" />
              {ingQuery.trim().length > 0 && ingResults.length > 0 && (
                <div className="mt-1 space-y-1 max-h-44 overflow-y-auto bg-black/40 rounded-lg p-2 border border-white/10">
                  {ingResults.slice(0, 12).map((f, idx) => (
                    <button key={`${f.name}-${idx}`} onClick={() => addIngredient(f)} className="w-full text-left text-xs p-2 rounded hover:bg-white/10" data-testid={`ingredient-result-${idx}`}>
                      <span className="text-white">{f.name}</span>
                      <span className="text-gray-400 ml-2">{f.calories} kcal · {f.protein}P / {f.carbs}C / {f.fats}G por {f.unit}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Lista de ingredientes */}
            <div className="space-y-2">
              {recipeItems.map((it, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`recipe-ingredient-${idx}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-white text-sm font-medium truncate flex-1">{it.food_name}</p>
                    <Button size="sm" variant="outline" onClick={() => removeIngredient(idx)} className="border-red-500/30 text-red-300 hover:bg-red-500/10">
                      <FaTrash />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min="0" step="any" value={it.quantity} onChange={(e) => updateIngredient(idx, { quantity: e.target.value })} className="bg-white/5 border-white/20 text-white text-sm" />
                    <select value={it.unit} onChange={(e) => updateIngredient(idx, { unit: e.target.value })} className="rounded-md px-3 py-2 bg-white/5 border border-white/20 text-white text-sm">
                      <option value="g">gramos</option>
                      <option value="ml">ml</option>
                      <option value="unidades">unidades</option>
                    </select>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    {it.calories} kcal · {it.protein}g P · {it.carbs}g C · {it.fats}g G
                  </p>
                </div>
              ))}
              {recipeItems.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6">Agrega ingredientes desde el buscador de arriba.</p>
              )}
            </div>

            {/* Total */}
            {recipeItems.length > 0 && (
              <div className="grid grid-cols-4 gap-2 p-3 rounded-xl bg-black/40 border border-pink-500/30">
                {['calories', 'protein', 'carbs', 'fats'].map((k) => (
                  <div key={k} className="text-center">
                    <p className="text-xl font-extrabold" style={{ color: MACRO_COLORS[k] }}>
                      {k === 'calories' ? Math.round(recipeTotals[k]) : recipeTotals[k].toFixed(1)}{k === 'calories' ? '' : 'g'}
                    </p>
                    <p className="text-[10px] uppercase text-gray-400">{k === 'calories' ? 'Kcal totales' : k === 'protein' ? 'Proteína' : k === 'carbs' ? 'Carbos' : 'Grasas'}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={() => handleSaveRecipe(false)} disabled={savingRecipe} variant="outline" className="border-pink-500/30 text-pink-200 hover:bg-pink-500/10" data-testid="save-recipe-private-btn">
                <FaSave className="mr-2" /> Guardar receta
              </Button>
              {/* Botón "guardar para todas" solo aparece para admin (el backend lo valida; aquí lo dejamos siempre por simplicidad — el server ignora si no eres admin) */}
              <Button onClick={() => handleSaveRecipe(true)} disabled={savingRecipe} variant="outline" className="border-purple-500/30 text-purple-200 hover:bg-purple-500/10" data-testid="save-recipe-public-btn">
                <FaBookOpen className="mr-2" /> Guardar para todas
              </Button>
              <Button onClick={handleAddRecipeToLog} disabled={saving || recipeItems.length === 0} className="bg-pink-500 hover:bg-pink-600 text-white ml-auto" data-testid="add-recipe-to-log-btn">
                <FaCheck className="mr-2" /> Agregar a mi día
              </Button>
            </div>
          </TabsContent>

          {/* ===== MANUAL ===== */}
          <TabsContent value="manual" className="flex-1 overflow-y-auto pr-1 space-y-3 mt-3">
            <Input value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} placeholder="Nombre (opcional)" className="bg-white/5 border-white/20 text-white" data-testid="manual-name-input" />
            <div className="grid grid-cols-2 gap-3">
              {[
                ['calories', 'Calorías (kcal)'],
                ['protein', 'Proteína (g)'],
                ['carbs', 'Carbos (g)'],
                ['fats', 'Grasas (g)'],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs mb-1 block" style={{ color: MACRO_COLORS[k] }}>{label}</label>
                  <Input type="number" min="0" step="any" value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} className="bg-white/5 border-white/20 text-white" data-testid={`manual-${k}-input`} />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-500">Si solo conoces las calorías, déjalas y los demás en 0.</p>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => { onOpenChange(false); resetAll(); }} className="border-white/20 text-white hover:bg-white/10">
            Cerrar
          </Button>
          {tab === 'manual' && (
            <Button onClick={handleSaveManual} disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="save-manual-btn">
              <FaCheck className="mr-2" /> Registrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
